import { useSyncExternalStore } from 'react';

export const ACCENTS = ['blue', 'teal', 'violet', 'amber', 'green'] as const;
export type Accent = (typeof ACCENTS)[number];

export const ACCENT_STORAGE_KEY = 'ohif-accent';
const DEFAULT_ACCENT: Accent = 'blue';

function toAccent(value: string | null | undefined): Accent {
  return (ACCENTS as readonly string[]).includes(value) ? (value as Accent) : DEFAULT_ACCENT;
}

/**
 * The accent lives on <html data-accent="...">; CSS re-values the ui-next
 * tokens from that attribute and React components observe it.
 */
export function getAccent(): Accent {
  return toAccent(document.documentElement.dataset.accent);
}

export function setAccent(accent: Accent): void {
  document.documentElement.dataset.accent = accent;
  try {
    localStorage.setItem(ACCENT_STORAGE_KEY, accent);
  } catch {
    // Storage blocked (private mode, policy) - accent still applies for this session.
  }
}

/** Restores the persisted accent. Call once before the app renders. */
export function initAccent(): void {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(ACCENT_STORAGE_KEY);
  } catch {
    // fall through to the default
  }
  document.documentElement.dataset.accent = toAccent(stored);
}

function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-accent'],
  });
  return () => observer.disconnect();
}

export function useAccent(): [Accent, (accent: Accent) => void] {
  const accent = useSyncExternalStore(subscribe, getAccent, () => DEFAULT_ACCENT);
  return [accent, setAccent];
}
